import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { Rail } from '../shared/Rail';
import { Section } from '../shared/Section';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useLayerY } from '../../hooks/useParallax';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';

/** Eén stem op de rail: een review, of een vaste cliënt met haar favoriete product. */
type Voice = {
  kind: 'review';
  quote: string;
  name: string;
  context: string;
} | {
  kind: 'client';
  quote: string;
  name: string;
  context: string;
  pickId: string;
};

/**
 * Reviews, na Opbouwen en vóór "één product": de score als kop, daarna alle stemmen als kaarten op de rail.
 * Geen automatisch doordraaien: je leest in je eigen tempo. Het grote aanhalingsteken ligt als
 * achterlaag achter de kaarten en beweegt trager mee. Zonder echte reviews de kop uit Paginateksten.
 */
export function HomeReviews() {
  const pages = usePages();
  const {
    shopRating,
    productById
  } = useCatalog();
  const {
    creators
  } = useSite();
  const copy = pages.home.reviews;
  // Reviews eerst, daarna vaste cliënten met hun favoriet (zonder sterren: het is geen review).
  // Een cliënt zonder bestaand product valt weg. De cliënten zelf: Instellingen › Cadeaus & codes.
  const clients: Voice[] = creators.flatMap(c => {
    const pick = productById(c.pickId);
    return pick ? [{
      kind: 'client' as const,
      quote: c.quote,
      name: c.name,
      context: `Vaste cliënt · haar favoriet: ${pick.name}`,
      pickId: pick.id
    }] : [];
  });
  const quotes: Voice[] = [...pages.testimonials.map(t => ({
    kind: 'review' as const,
    ...t
  })), ...clients];
  const count = shopRating.count;
  const avg = rating(shopRating.average);
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const glyphY = useLayerY(scrollYProgress, [-24, 48]);
  return <Section ref={ref} tone="shell" labelledBy="home-reviews" contained={false} data-id="element-7461" data-mp="15cab6qke6xvl">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7462" data-mp="10fjtjysxvu82">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7463" data-mp="vgysxy9rz8j3">
          <div className="lg:col-span-7" data-id="element-7464" data-mp="1t3o16pv2mhde">
            {count > 0 ? <>
                {/* De sterren zijn beeld; de kop eronder zegt hetzelfde in woorden. */}
                <p className="flex items-center gap-1" aria-hidden="true" data-id="element-7465" data-mp="1o30bq8mhq8la">
                  {Array.from({
                length: 5
              }, (_, i) => <StarIcon key={i} className="h-4 w-4 fill-current text-clay" strokeWidth={0} data-id="element-7466" data-mp="1e76tc8m3bkvl" />)}
                </p>
                <h2 id="home-reviews" className="mt-4 type-sub text-ink" data-id="element-7467" data-mp="7s60wcw9xktr">
                  {avg} uit {plural(count, 'beoordeling', 'beoordelingen')}
                  <span className="sr-only" data-id="element-7468" data-mp="2efdhvw3ih096"> (van 5 sterren)</span>
                </h2>
              </> : <h2 id="home-reviews" className="type-sub text-ink" data-id="element-7469" data-mp="2jezvw7r5ykj1">
                <HeadingText heading={copy.heading} data-id="element-7470" data-mp="2odb4kf5q2ozo" />
              </h2>}
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-7471" data-mp="2t49pjs1nob99">{fill(copy.body)}</p>}
        </div>

        {quotes.length > 0 && <div className="relative mt-12 lg:mt-16" data-id="element-7472" data-mp="2y4xapx37dxvk">
            <motion.span aria-hidden="true" style={glyphY ? {
          y: glyphY
        } : undefined} className="ornament-quote pointer-events-none absolute -top-14 left-0 text-clay sm:-top-20" data-id="element-7473" data-mp="323o9e8nbhnks">
              “
            </motion.span>
            <Rail id="home-reviews-rail" label="Reviews" className="relative" items={quotes} itemKey={t => `${t.name}-${t.context}`} itemClassName="w-[86%] sm:w-[62%] lg:w-[calc((100%-3rem)/2.5)]" renderItem={t => <figure className="flex w-full flex-col bg-sand p-6 sm:p-8" data-id="element-7475" data-mp="337erylz6bq2f">
                  <blockquote className="type-title leading-snug text-ink" data-id="element-7476" data-mp="3h64ljrmsb7m1">{fill(t.quote)}</blockquote>
                  <figcaption className="mt-auto pt-6 text-[14px] text-muted" data-id="element-7477" data-mp="3d49mktsbt6bf">
                    {t.kind === 'client' ? <>
                        {t.name} ·{' '}
                        <Link to={`/webshop/${t.pickId}`} className="underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-7478" data-mp="2kgdszxy1zuiv">
                          {t.context}
                        </Link>
                      </> : <>
                        {t.name} · {t.context}
                      </>}
                  </figcaption>
                </figure>} data-id="element-7474" data-mp="1cprlky9wp33j" />
          </div>}
      </div>
    </Section>;
}